import { useState } from 'react';
import { numberError } from '../config/ConfigKeyEditor';
import ConfigHistoryDialog from '../config/ConfigHistoryDialog';
import { useConfigCard } from '../config/useConfigCard';
import { CardStateFrame, ConfigCardFrame, ForbiddenNotice, NumberField } from './ConfigCardParts';
import ConfirmChangesDialog, { ChangeLine } from './ConfirmChangesDialog';
import { DEALER_CREDIT_DEFAULTS, DEALER_CREDIT_KEYS } from './financeConfigDefaults';

const K = DEALER_CREDIT_KEYS;
const KEYS = Object.values(DEALER_CREDIT_KEYS);
/** The kill switch: shown, never edited here (a `dynamic-config` key, flipped only on Maher's written go). */
export const SWITCH_KEY = 'garagat.dealer-credit.enabled';
/** The wallet switch (packs, prepaid balance, assign from it; Maher 2026-11-03): also shown, never edited here. */
export const WALLET_SWITCH_KEY = 'garagat.dealer-credit.wallet.enabled';
const WATCH = [SWITCH_KEY, WALLET_SWITCH_KEY];

const LABELS: Record<string, string> = {
  [K.validityMonths]: 'Buyer credit validity',
  [K.unassignedMonths]: 'Unassigned dealer balance validity',
  [K.autoRevokeDays]: 'Auto-revoke unpaid requests after',
  [K.refundRestoreMinDays]: 'Days left when credit is restored on a refund',
  [K.minAmountAed]: 'Smallest per-buyer credit',
  [K.saleVatRate]: 'VAT on credit sold to dealers',
  [K.expiryDisposition]: 'On expiry',
  [K.expiryTreatment]: 'Books treatment of kept credit',
};

/** Whole-number keys: orders falls back to the default on anything else (`DealerCreditSettings.wholeNumber`). */
const WHOLE = [K.validityMonths, K.unassignedMonths, K.autoRevokeDays, K.refundRestoreMinDays] as const;
const NUMBER_FIELDS = [...WHOLE, K.minAmountAed, K.saleVatRate] as const;

const DISPOSITION_LABELS: Record<string, string> = {
  KEEP: 'Garagat keeps it (income)',
  RETURN_TO_DEALER: "Back to the dealer's balance",
};
const TREATMENT_LABELS: Record<string, string> = {
  BREAKAGE_INCOME: 'All of it is income',
  BREAKAGE_INCOME_VAT_INCLUSIVE: '5% VAT taken out first',
};

function options(allowed: string[] | undefined, labels: Record<string, string>) {
  const known = Object.keys(labels);
  const values = allowed && allowed.length ? known.filter((v) => allowed.includes(v)).concat(allowed.filter((v) => !known.includes(v))) : known;
  return values.map((v) => ({ value: v, label: labels[v] ?? v }));
}

const fieldId = (key: string) => `dealer-credit-${key.slice('garagat.dealer-credit.'.length)}`;

/**
 * Spec 085 (G15, BR-35, BR-30, BR-31) — Finance → Configuration: the dealer credit settings, on the
 * `finance-config` screen like the service fee and Tabby. All eight keys save in one atomic request
 * after a confirm listing every change; each applies to credit created or expiring from then on.
 *
 * The kill switch `garagat.dealer-credit.enabled` is deliberately not editable here: it is a
 * `dynamic-config` key, changed in Runtime Config only on Maher's written go. The card shows its
 * state when the admin may read it, and says where it lives otherwise. The same goes for the wallet switch
 * `garagat.dealer-credit.wallet.enabled` (credit packs, the prepaid dealer balance, assigning from it).
 */
export default function DealerCreditCard({ token }: { token: string }) {
  const card = useConfigCard({ keys: KEYS, screen: 'finance-config', defaults: DEALER_CREDIT_DEFAULTS, token, watch: WATCH });
  const { draft, server, entries } = card;
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState(false);

  if (card.load.kind === 'loading') {
    return (
      <CardStateFrame testId="dealer-credit-card" title="Dealer credit">
        <div className="admin-loading" aria-live="polite"><div className="admin-loading-spinner" /></div>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'error') {
    return (
      <CardStateFrame testId="dealer-credit-card" title="Dealer credit">
        <div className="admin-info-banner danger" role="alert" data-testid="dealer-credit-load-error">{card.load.message}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={() => card.reload()}>Try again</button>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'missing') {
    return (
      <CardStateFrame testId="dealer-credit-card" title="Dealer credit">
        <div className="admin-info-banner warning" role="status" data-testid="dealer-credit-unavailable">
          The dealer credit settings are not available to your account on this environment. Reload the page, or ask a super admin.
        </div>
      </CardStateFrame>
    );
  }

  const v = (key: string) => draft[key] ?? '';
  const errorOf = (key: string): string | null => {
    if (card.fieldErrors[key]) return card.fieldErrors[key];
    const e = entries.get(key);
    return e && (NUMBER_FIELDS as readonly string[]).includes(key)
      ? numberError(e, v(key), { whole: (WHOLE as readonly string[]).includes(key) })
      : null;
  };
  const invalid = NUMBER_FIELDS.filter((k) => errorOf(k));
  const changes = card.changesFrom(draft);
  const dirty = changes.length > 0;
  const deployed = (key: string) => !entries.get(key)?.overridden;
  const range = (key: string) => {
    const e = entries.get(key);
    return e && e.min !== null && e.max !== null ? ` ${e.min}–${e.max}.` : '';
  };

  const switchEntry = card.watched.get(SWITCH_KEY);
  const switchOn = switchEntry ? (switchEntry.value ?? 'false').trim() === 'true' : null;
  const walletEntry = card.watched.get(WALLET_SWITCH_KEY);
  const walletOn = walletEntry ? (walletEntry.value ?? 'false').trim() === 'true' : null;

  const fmt = (key: string, value: string) => {
    switch (key) {
      case K.validityMonths:
      case K.unassignedMonths: return `${value} months`;
      case K.autoRevokeDays:
      case K.refundRestoreMinDays: return `${value} days`;
      case K.minAmountAed: return `AED ${value}`;
      case K.saleVatRate: return `${value} (${Math.round(Number(value) * 10000) / 100}%)`;
      case K.expiryDisposition: return DISPOSITION_LABELS[value] ?? value;
      case K.expiryTreatment: return TREATMENT_LABELS[value] ?? value;
      default: return value;
    }
  };
  const changeLines = (): ChangeLine[] => changes.map((c) => ({ label: LABELS[c.key], from: fmt(c.key, server[c.key]), to: fmt(c.key, c.value) }));

  const submit = () => {
    if (invalid.length) {
      window.setTimeout(() => document.getElementById(fieldId(invalid[0]))?.focus(), 0);
      return;
    }
    if (dirty) setConfirming(true);
  };

  const numberField = (key: string, label: string, unit: string, unitBefore: boolean, hint: string) => (
    <NumberField
      id={fieldId(key)}
      label={label}
      unit={unit}
      unitBefore={unitBefore}
      value={v(key)}
      onChange={(val) => card.setField(key, val)}
      hint={`${hint}${range(key)}`}
      error={errorOf(key)}
      deployed={deployed(key)}
    />
  );

  const select = (key: string, label: string, labels: Record<string, string>, hint: string) => (
    <div className="config-field">
      <label htmlFor={fieldId(key)}>{label}</label>
      <select id={fieldId(key)} className="config-select" value={v(key)} aria-describedby={`${fieldId(key)}-hint`}
        onChange={(e) => card.setField(key, e.target.value)}>
        {options(entries.get(key)?.allowedValues, labels).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <div className="config-hint" id={`${fieldId(key)}-hint`}>
        {hint}
        {deployed(key) && <span className="config-deployed"> Using the deployed value.</span>}
      </div>
    </div>
  );

  return (
    <>
      <ConfigCardFrame
        testId="dealer-credit-card"
        title="Dealer credit"
        status={(
          <span className={`admin-badge ${switchOn ? 'approved' : 'neutral'}`} data-testid="dealer-credit-status">
            {switchOn === null ? 'Switch in Runtime Config' : switchOn ? 'Switched on' : 'Switched off'}
          </span>
        )}
        dirty={dirty}
        sub="How long dealer credit lasts, when unpaid requests lapse, what happens on expiry, and the VAT on credit sold to dealers."
        lastChanged={card.lastChanged}
        onHistory={() => setHistory(true)}
        footNote="Live within a minute. Applies to credit created or expiring from then on. Same audit as Runtime Config."
        footError={card.formError}
        invalidCount={invalid.length}
        saving={card.saving}
        canSave={dirty && invalid.length === 0 && !card.forbidden}
        onDiscard={card.discard}
        onSubmit={submit}
        toast={card.toast}
      >
        {card.forbidden && <ForbiddenNotice onReload={() => card.reload()} />}

        <div className="config-readonly-row" data-testid="dealer-credit-switch">
          <div className="config-switch-label">Dealer credit (kill switch)</div>
          <div className="config-hint">
            {switchOn === null
              ? 'Its state is shown to Runtime Config admins only. '
              : <>Currently <strong>{switchOn ? 'on' : 'off'}</strong>. </>}
            Not changed here: it is flipped in Runtime Config only on Maher&apos;s written go. Off hides the dealer screens and the
            credit at checkout for new bookings; credit already issued keeps settling, refunding and expiring.
          </div>
        </div>

        <div className="config-readonly-row" data-testid="dealer-credit-wallet-switch">
          <div className="config-switch-label">Dealer wallet (credit packs and prepaid balance)</div>
          <div className="config-hint">
            {walletOn === null
              ? 'Its state is shown to Runtime Config admins only. '
              : <>Currently <strong>{walletOn ? 'on' : 'off'}</strong>{walletOn && switchOn === false ? ', but inactive while the kill switch is off' : ''}. </>}
            Not changed here: it is flipped in Runtime Config only on Maher&apos;s written go. Off stops dealers buying packs,
            holding a prepaid balance and assigning from it; per-buyer requests and mark-paid carry on.
          </div>
        </div>

        <h5>Validity</h5>
        <div className="config-grid">
          {numberField(K.validityMonths, 'Buyer credit validity', 'months', false, 'From the day it goes live.')}
          {numberField(K.unassignedMonths, 'Unassigned dealer balance', 'months', false, 'A pack balance not yet given to a buyer.')}
          {numberField(K.refundRestoreMinDays, 'Restored on refund, at least', 'days', false, 'Days left on credit given back after a refund.')}
        </div>
        <h5>Requests</h5>
        <div className="config-grid">
          {numberField(K.autoRevokeDays, 'Auto-revoke unpaid after', 'days', false, 'Unpaid requests are cancelled and their proforma voided.')}
          {numberField(K.minAmountAed, 'Smallest per-buyer credit', 'AED', true, 'Whole AED a dealer may request for one buyer.')}
        </div>
        <h5>Expiry and VAT (accountant&apos;s call)</h5>
        <div className="config-grid">
          {select(K.expiryDisposition, 'Unused credit on expiry', DISPOSITION_LABELS, 'Applies to credit expiring from now on.')}
          {select(K.expiryTreatment, 'Books treatment of kept credit', TREATMENT_LABELS, 'Set only on the accountant’s advice.')}
          {numberField(K.saleVatRate, 'VAT on credit sold to dealers', '', false, 'A fraction: 0.05 is 5%, 0 is no VAT. Only on the accountant’s advice.')}
        </div>
      </ConfigCardFrame>

      {confirming && (
        <ConfirmChangesDialog
          title="Save the dealer credit settings?"
          body="These apply within a minute to credit created or expiring from then on. Credit already issued keeps the terms frozen on it."
          confirmLabel="Save changes"
          changes={changeLines()}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); void card.save(changes); }}
        />
      )}
      {history && (
        <ConfigHistoryDialog
          title="Dealer credit history"
          keys={KEYS}
          labels={LABELS}
          token={token}
          onClose={() => setHistory(false)}
        />
      )}
    </>
  );
}
